// Bodega — Ciclo Semanal de Inventario con QR (Bloque 3)
// Dashboard por pañol + vista de ciclo activo con modo scan.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  ClipboardCheck, QrCode, Play, XCircle, CheckCircle2, AlertTriangle,
  Loader2, ArrowLeft, Scan, Trash2, Flag, Clock, PackagePlus,
} from 'lucide-react';
import { toast } from 'sonner';

const SEMAFORO = {
  verde: { bg: 'bg-emerald-500', text: 'text-emerald-700', border: 'border-l-emerald-500', label: 'Al día' },
  amarillo: { bg: 'bg-amber-500', text: 'text-amber-700', border: 'border-l-amber-500', label: 'Próximo' },
  rojo: { bg: 'bg-rose-500', text: 'text-rose-700', border: 'border-l-rose-500', label: 'Atrasado' },
};

export default function InventarioSemanalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const panolIdQP = searchParams.get('panol_id') || '';
  const autoStart = searchParams.get('auto_start') === 'true';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activo, setActivo] = useState(null);
  const [stats, setStats] = useState(null);
  const [scanInput, setScanInput] = useState('');
  const [lastScan, setLastScan] = useState(null);
  const [confirmCerrar, setConfirmCerrar] = useState(false);
  const [iniciando, setIniciando] = useState(false);
  const inputRef = useRef(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/bodega/inventario-ciclo/dashboard');
      setItems(res.data.items || []);
    } catch (err) {
      toast.error('Error cargando dashboard');
    } finally { setLoading(false); }
  }, []);

  const [loadedActivo, setLoadedActivo] = useState(false);

  const loadActivo = useCallback(async (panol_id) => {
    try {
      const res = await api.get(`/bodega/inventario-ciclo/activo/${panol_id}`);
      setActivo(res.data.ciclo);
      setStats(res.data.stats || null);
    } catch (err) {
      toast.error('Error cargando ciclo activo');
    } finally {
      setLoadedActivo(true);
    }
  }, []);

  useEffect(() => {
    if (!panolIdQP) {
      loadDashboard();
    } else {
      loadActivo(panolIdQP);
    }
  }, [panolIdQP, loadDashboard, loadActivo]);

  // Auto-start: si vienen del email con ?auto_start=true y no hay ciclo abierto, iniciarlo.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStart && panolIdQP && loadedActivo && activo === null && !autoStartedRef.current && !iniciando) {
      autoStartedRef.current = true;
      iniciarCiclo(panolIdQP);
      const next = new URLSearchParams(searchParams);
      next.delete('auto_start');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, panolIdQP, activo, loadedActivo]);

  // Auto-focus en input scan cuando hay ciclo abierto
  useEffect(() => {
    if (activo && activo.estado === 'abierto' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [activo]);

  const abrirPanol = (panolId) => {
    setSearchParams({ panol_id: panolId });
  };

  const volverDashboard = () => {
    setActivo(null);
    setStats(null);
    setSearchParams({});
    loadDashboard();
  };

  const iniciarCiclo = async (panolId) => {
    setIniciando(true);
    try {
      const res = await api.post('/bodega/inventario-ciclo/iniciar', { panol_id: panolId });
      setActivo(res.data.ciclo);
      setStats(res.data.stats || null);
      toast.success(`Ciclo iniciado · ${res.data.stats?.total_esperados || 0} htas esperadas`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error iniciando ciclo');
    } finally { setIniciando(false); }
  };

  const hacerScan = async (codigo) => {
    if (!activo?.id) return;
    const c = (codigo || '').trim();
    if (!c) return;
    try {
      const res = await api.post(`/bodega/inventario-ciclo/${activo.id}/scan`, {
        codigo: c, origen: 'manual',
      });
      setStats(res.data.stats);
      setLastScan(res.data);
      setScanInput('');
      if (res.data.estado === 'ok') {
        toast.success(`OK · ${res.data.descripcion || c}`);
      } else if (res.data.estado === 'sobrante') {
        toast.warning(`Sobrante · ${c} no estaba en este pañol`);
      } else if (res.data.estado === 'ya_escaneado') {
        toast.info(`${c} ya fue escaneada`);
      }
      // Refrescar esperados en local (no reload completo)
      setActivo((prev) => {
        if (!prev) return prev;
        const exists = (prev.scans || []).some((s) => s.codigo === c);
        if (res.data.estado !== 'ya_escaneado' && !exists) {
          return {
            ...prev,
            scans: [...(prev.scans || []), {
              codigo: c, estado: res.data.estado,
              escaneado_en: new Date().toISOString(),
            }],
          };
        }
        return prev;
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error en scan');
    } finally {
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const deshacerScan = async (codigo) => {
    if (!activo?.id) return;
    try {
      const res = await api.delete(`/bodega/inventario-ciclo/${activo.id}/scan/${encodeURIComponent(codigo)}`);
      setStats(res.data.stats);
      setActivo((prev) => prev ? {
        ...prev, scans: (prev.scans || []).filter((s) => s.codigo !== codigo),
      } : prev);
      toast.success('Scan deshecho');
    } catch (err) {
      toast.error('Error deshaciendo scan');
    }
  };

  const cerrarCiclo = async () => {
    if (!activo?.id) return;
    try {
      const res = await api.post(`/bodega/inventario-ciclo/${activo.id}/cerrar`);
      toast.success(`Ciclo cerrado · ${res.data.resumen.ok_count}/${res.data.resumen.total_esperados} OK`);
      setConfirmCerrar(false);
      volverDashboard();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cerrando ciclo');
    }
  };

  // ═══════════════════════════════════════════════════════
  // Vista 1: Dashboard
  // ═══════════════════════════════════════════════════════
  if (!panolIdQP) {
    return (
      <div className="p-6 space-y-6" data-testid="inventario-semanal-dashboard">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8 text-indigo-600" />
            Ciclo de Inventario Semanal
          </h1>
          <p className="text-slate-500 mt-1">
            Semáforo por pañol. Verde: chequeado hace ≤7 días. Amarillo: 7-14. Rojo: &gt;14 días o nunca.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
          </div>
        ) : items.length === 0 ? (
          <Card><CardContent className="p-6 text-slate-500">No hay pañoles activos.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((p) => {
              const sem = SEMAFORO[p.semaforo] || SEMAFORO.rojo;
              return (
                <Card
                  key={p.panol_id}
                  className={`cursor-pointer hover:shadow-lg transition-shadow border-l-4 ${sem.border}`}
                  onClick={() => abrirPanol(p.panol_id)}
                  data-testid={`card-panol-${p.panol_id}`}
                >
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-bold text-lg">{p.panol_nombre}</div>
                        {p.responsable_nombre && (
                          <div className="text-xs text-slate-500">Resp: {p.responsable_nombre}</div>
                        )}
                      </div>
                      <div className={`w-3 h-3 rounded-full ${sem.bg}`} title={sem.label} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <div className="text-xs text-slate-500">Htas asignadas</div>
                        <div className="font-semibold">{p.total_herramientas}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Días desde cierre</div>
                        <div className={`font-semibold ${sem.text}`}>
                          {p.dias_desde_cierre === null ? '— nunca' : `${p.dias_desde_cierre}d`}
                        </div>
                      </div>
                    </div>
                    {p.ciclo_abierto ? (
                      <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 w-full justify-center py-1">
                        <Scan className="w-3 h-3 mr-1" /> Ciclo en curso · {p.ciclo_abierto.stats.pct_completado}%
                      </Badge>
                    ) : p.ultimo_cierre ? (
                      <div className="text-xs text-slate-500">
                        Último: {p.ultimo_cierre.semana_iso} ·{' '}
                        {p.ultimo_cierre.resumen?.ok_count || 0}/{p.ultimo_cierre.resumen?.total_esperados || 0} OK
                      </div>
                    ) : (
                      <div className="text-xs text-rose-600 italic">Sin ciclos previos</div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════
  // Vista 2: Pañol sin ciclo abierto → invita a iniciar
  // ═══════════════════════════════════════════════════════
  const panolEnDashboard = items.find((p) => p.panol_id === panolIdQP);
  const panolNombre = activo?.panol_nombre || panolEnDashboard?.panol_nombre || panolIdQP;

  if (!activo) {
    return (
      <div className="p-6 space-y-4">
        <Button variant="ghost" onClick={volverDashboard} data-testid="btn-volver">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver al dashboard
        </Button>
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <ClipboardCheck className="w-16 h-16 mx-auto text-slate-400" />
            <h2 className="text-2xl font-bold">{panolNombre}</h2>
            <p className="text-slate-500">No hay ciclo de inventario abierto.</p>
            <Button
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={iniciando}
              onClick={() => iniciarCiclo(panolIdQP)}
              data-testid="btn-iniciar-ciclo"
            >
              {iniciando ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Iniciando...</>
              ) : (
                <><Play className="w-5 h-5 mr-2" /> Iniciar Ciclo Semanal</>
              )}
            </Button>
            <p className="text-xs text-slate-400">
              Se tomará un snapshot de las herramientas actuales asignadas a este pañol.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════
  // Vista 3: Ciclo abierto → modo scan
  // ═══════════════════════════════════════════════════════
  const esperadosMap = Object.fromEntries((activo.esperados || []).map((e) => [e.codigo, e]));
  const escaneadosSet = new Set((activo.scans || []).map((s) => s.codigo));
  const pendientes = (activo.esperados || []).filter((e) => !escaneadosSet.has(e.codigo));
  const sobrantes = (activo.scans || []).filter((s) => !esperadosMap[s.codigo]);
  const escaneadasOk = (activo.scans || []).filter((s) => esperadosMap[s.codigo]);

  return (
    <div className="p-6 space-y-6" data-testid="ciclo-activo-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={volverDashboard} className="mb-2" data-testid="btn-volver">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Scan className="w-6 h-6 text-indigo-600" /> {activo.panol_nombre}
          </h1>
          <div className="text-sm text-slate-500 mt-1 flex items-center gap-3">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {activo.semana_iso}</span>
            <span>Iniciado por: {activo.iniciado_por}</span>
          </div>
        </div>
        <Button
          variant="destructive"
          onClick={() => setConfirmCerrar(true)}
          data-testid="btn-cerrar-ciclo"
        >
          <Flag className="w-4 h-4 mr-1" /> Cerrar ciclo
        </Button>
      </div>

      {/* Progress bar */}
      {stats && (
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-5xl font-bold text-emerald-600" data-testid="pct-completado">
                  {stats.pct_completado}%
                </div>
                <div className="text-sm text-slate-500">
                  {stats.ok_count} de {stats.total_esperados} esperadas
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-2xl font-bold text-emerald-600">{stats.ok_count}</div>
                  <div className="text-xs text-slate-500">OK</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-amber-600">{stats.faltantes_count}</div>
                  <div className="text-xs text-slate-500">Faltantes</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-rose-600">{stats.sobrantes_count}</div>
                  <div className="text-xs text-slate-500">Sobrantes</div>
                </div>
              </div>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${stats.pct_completado}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scan input */}
      <Card className="bg-indigo-50 border-indigo-200">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
            <QrCode className="w-4 h-4" /> Escanea o ingresa código
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); hacerScan(scanInput); }}
            className="flex gap-2"
          >
            <Input
              ref={inputRef}
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              placeholder="Código de herramienta (ej: 0102104801250000001)"
              className="text-lg font-mono"
              autoComplete="off"
              data-testid="input-scan"
            />
            <Button type="submit" disabled={!scanInput.trim()} data-testid="btn-scan">
              <PackagePlus className="w-4 h-4 mr-1" /> Registrar
            </Button>
          </form>
          {lastScan && (
            <div
              className={`text-sm p-2 rounded border ${
                lastScan.estado === 'ok' ? 'bg-emerald-100 border-emerald-300 text-emerald-800'
                : lastScan.estado === 'sobrante' ? 'bg-rose-100 border-rose-300 text-rose-800'
                : 'bg-slate-100 border-slate-300 text-slate-700'
              }`}
              data-testid="last-scan-feedback"
            >
              {lastScan.estado === 'ok' && <CheckCircle2 className="inline w-4 h-4 mr-1" />}
              {lastScan.estado === 'sobrante' && <AlertTriangle className="inline w-4 h-4 mr-1" />}
              <b>{lastScan.codigo}</b> · {lastScan.descripcion || '(sin descripción)'}
              {lastScan.asignado_a_nombre && <span className="text-xs"> · {lastScan.asignado_a_nombre}</span>}
              {lastScan.estado === 'ya_escaneado' && <span className="italic"> (ya registrado)</span>}
              {lastScan.estado === 'sobrante' && <span className="italic"> (no estaba en este pañol)</span>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pendientes */}
        <Card data-testid="list-pendientes">
          <CardContent className="p-4">
            <h3 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Pendientes ({pendientes.length})
            </h3>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {pendientes.length === 0 ? (
                <div className="text-xs text-slate-400 italic">¡Todas escaneadas!</div>
              ) : pendientes.slice(0, 50).map((e) => (
                <div key={e.codigo} className="text-xs border-b py-1.5">
                  <div className="font-mono text-indigo-700">{e.codigo}</div>
                  <div className="text-slate-600 truncate">{e.descripcion}</div>
                  {e.asignado_a_nombre && (
                    <div className="text-[10px] text-slate-400">{e.asignado_a_nombre}</div>
                  )}
                </div>
              ))}
              {pendientes.length > 50 && (
                <div className="text-xs text-slate-400 italic pt-1">
                  +{pendientes.length - 50} más...
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Escaneadas OK */}
        <Card data-testid="list-ok">
          <CardContent className="p-4">
            <h3 className="font-semibold text-emerald-700 mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Escaneadas ({escaneadasOk.length})
            </h3>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {escaneadasOk.length === 0 ? (
                <div className="text-xs text-slate-400 italic">Sin scans aún.</div>
              ) : escaneadasOk.slice().reverse().slice(0, 50).map((s) => (
                <div key={s.codigo} className="text-xs border-b py-1.5 flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-emerald-700">{s.codigo}</div>
                    <div className="text-slate-600 truncate">{esperadosMap[s.codigo]?.descripcion}</div>
                  </div>
                  <button
                    onClick={() => deshacerScan(s.codigo)}
                    className="text-rose-500 hover:text-rose-700"
                    title="Deshacer"
                    data-testid={`btn-undo-${s.codigo}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Sobrantes */}
        <Card data-testid="list-sobrantes">
          <CardContent className="p-4">
            <h3 className="font-semibold text-rose-700 mb-3 flex items-center gap-2">
              <XCircle className="w-4 h-4" /> Sobrantes ({sobrantes.length})
            </h3>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {sobrantes.length === 0 ? (
                <div className="text-xs text-slate-400 italic">Sin sobrantes.</div>
              ) : sobrantes.slice().reverse().map((s) => (
                <div key={s.codigo} className="text-xs border-b py-1.5 flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-rose-700">{s.codigo}</div>
                    <div className="text-slate-400 italic text-[10px]">No estaba en este pañol</div>
                  </div>
                  <button
                    onClick={() => deshacerScan(s.codigo)}
                    className="text-rose-500 hover:text-rose-700"
                    title="Deshacer"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cerrar ciclo confirm */}
      <AlertDialog open={confirmCerrar} onOpenChange={setConfirmCerrar}>
        <AlertDialogContent data-testid="dialog-confirm-cerrar">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cerrar el ciclo ahora?</AlertDialogTitle>
            <AlertDialogDescription>
              Se registrará el cierre con {stats?.ok_count || 0} OK,{' '}
              <b className="text-amber-700">{stats?.faltantes_count || 0} faltantes</b> y{' '}
              <b className="text-rose-700">{stats?.sobrantes_count || 0} sobrantes</b>.
              <br />No podrás agregar más scans después.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={cerrarCiclo} data-testid="btn-confirm-cerrar">
              Sí, cerrar ciclo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
